import type { ReactNode } from 'react';
import { useMemo } from 'react';
import {
  DEFAULT_SLOT,
  OverlayTriggerStateContext,
  Provider,
} from 'react-aria-components';

import { cn } from '@infinex/ui-styles';
import {
  ButtonContext,
  ContentContext,
  DialogContext,
  DialogVars,
  FooterContext,
  ModalVars,
  type DialogContextValue,
} from '@infinex/ui/components';

export type StandaloneDialogProvidersProps = {
  /** Called when any child invokes `useDialogState().close()`. */
  onClose: () => void;
  children: ReactNode;
};

/**
 * Provides everything the `Dialog` component normally supplies — without
 * rendering any modal/overlay UI — so that `DialogRouter` content can be
 * rendered standalone (e.g. full-page or inside a native container).
 *
 * **Why this exists:** The mobile app renders web-app dialog content inside a
 * native action sheet via a WebView. If we kept the `Dialog` wrapper we would
 * get a dialog-inside-a-sheet — a modal overlay rendered within an
 * already-modal container.
 */
export function StandaloneDialogProviders({
  onClose,
  children,
}: StandaloneDialogProvidersProps) {
  const overlayState = useMemo(
    () => ({
      isOpen: true,
      open() {},
      close: onClose,
      toggle() {},
      setOpen(open: boolean) {
        if (!open) onClose();
      },
    }),
    [onClose]
  );

  const dialogContextValue = useMemo<DialogContextValue>(
    () => ({
      isDismissable: true,
      isKeyboardDismissDisabled: undefined,
      type: 'modal',
    }),
    []
  );

  return (
    <OverlayTriggerStateContext.Provider value={overlayState}>
      <DialogContext.Provider value={dialogContextValue}>
        <Provider
          values={[
            [
              ButtonContext,
              {
                slots: {
                  [DEFAULT_SLOT]: {},
                  close: { onPress: onClose },
                },
              },
            ],
            [
              ContentContext,
              {
                className: cn('flex-1', DialogVars.tw('p-(--Dialog-padding)')),
              },
            ],
            [
              FooterContext,
              {
                className: cn(
                  'flex shrink-0 items-center gap-2',
                  DialogVars.tw(
                    'px-(--Dialog-padding)',
                    'pb-(--Dialog-padding)'
                  )
                ),
              },
            ],
          ]}
        >
          <div
            style={{
              ...ModalVars.apply({
                maxHeight: '',
                maxWidth: '',
                padding: '',
                viewportGutter: '',
              }),
              ...DialogVars.apply((vars) => ({
                padding: 32,
                maxHeight: 'none',
                headerButton: 32,
                headerButtonSpacer: `calc(${vars.headerButton} + ${vars.headerGap})`,
                headerGap: 8,
                headerGutter: `calc(${vars.padding} / 2)`,
                headerPaddingX: `calc(${vars.padding} - ${vars.headerGutter})`,
              })),
            }}
          >
            {children}
          </div>
        </Provider>
      </DialogContext.Provider>
    </OverlayTriggerStateContext.Provider>
  );
}
